import React from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

const RoleDemandTrendChart = ({ data, description }) => {
  const roleTrendData = () => {
    const quarterlyData = {};
    const topRoles = {};
    
    // First, identify the top 5 most popular roles overall
    data.filter(candidate => 
      candidate.role && 
      candidate.role.trim() !== ''
    ).forEach(candidate => {
      const role = candidate.role;
      topRoles[role] = (topRoles[role] || 0) + 1;
    });
    
    // Get top 5 roles
    const top5Roles = Object.entries(topRoles)
      .sort(([,a], [,b]) => b - a)
      .slice(0, 5)
      .map(([role]) => role);
    
    // Group data by quarter for top roles
    data.filter(candidate => 
      candidate.role && 
      candidate.role.trim() !== ''
    ).forEach(candidate => {
      const role = candidate.role;
      const quarter = candidate.quarter || 'Unknown';
      
      if (!top5Roles.includes(role)) return;
      
      if (!quarterlyData[quarter]) {
        quarterlyData[quarter] = { quarter };
        top5Roles.forEach(r => quarterlyData[quarter][r] = 0);
      }
      
      quarterlyData[quarter][role]++;
    });
    
    // Convert to array and sort by quarter
    return Object.values(quarterlyData)
      .filter(item => item.quarter !== 'Unknown')
      .sort((a, b) => {
        // Custom sort for quarters (Q1 2024, Q2 2024, etc.)
        const aYear = parseInt(a.quarter.split(' ')[1]) || 0;
        const bYear = parseInt(b.quarter.split(' ')[1]) || 0;
        const aQ = parseInt(a.quarter.split('Q')[1]) || 0;
        const bQ = parseInt(b.quarter.split('Q')[1]) || 0;
        
        if (aYear !== bYear) return aYear - bYear;
        return aQ - bQ;
      });
  };

  // Color palette for different roles
  const roleColors = [
    '#3b82f6', '#22c55e', '#f59e0b', '#ef4444', '#8b5cf6'
  ];

  const chartData = roleTrendData();
  const roleNames = chartData.length > 0 ? 
    Object.keys(chartData[0]).filter(key => key !== 'quarter') : [];

  const totalCandidates = chartData.reduce((sum, quarter) => {
    return sum + roleNames.reduce((quarterSum, role) => quarterSum + (quarter[role] || 0), 0);
  }, 0);

  return (
    <div className="dashboard-card p-6 slide-in-up" style={{animationDelay: '1200ms'}}>
      <div className="flex justify-between items-start mb-4">
        <div>
          <h3 className="text-lg font-semibold text-gray-800 mb-1">Role Demand Trends</h3>
          <p className="text-sm text-gray-500">Top 5 roles tracked over time</p>
        </div>
        <div className="text-right">
          <div className="bg-purple-100 text-purple-800 text-xs font-medium px-2.5 py-1 rounded-full mb-1">
            Trends
          </div>
          <p className="text-xs text-gray-500">{totalCandidates} total candidates</p>
        </div>
      </div>
      {description && (
        <p className="text-sm text-gray-600 mb-4">{description}</p>
      )}
      <div className="bg-gray-50 rounded-lg p-4">
        <ResponsiveContainer width="100%" height={280}>
          <LineChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 60 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" opacity={0.6} />
            <XAxis 
              dataKey="quarter" 
              tickLine={false} 
              axisLine={false} 
              style={{ fontSize: '11px', fill: '#6b7280' }}
              tick={{ dy: 10 }}
            />
            <YAxis 
              tickLine={false} 
              axisLine={false} 
              style={{ fontSize: '11px', fill: '#6b7280' }}
              label={{ value: 'Number of Candidates', angle: -90, position: 'insideLeft', style: { textAnchor: 'middle', fontSize: '11px', fill: '#6b7280' } }}
            />
            <Tooltip 
              contentStyle={{ 
                borderRadius: '12px', 
                border: 'none', 
                boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
                fontSize: '12px',
                backgroundColor: 'white',
                padding: '12px'
              }}
              labelStyle={{ fontWeight: '600', color: '#374151', marginBottom: '4px' }}
              labelFormatter={(label) => `${label}`}
              formatter={(value, name) => [
                `${value} candidates`, 
                name.length > 20 ? name.substring(0, 20) + '...' : name
              ]}
            />
            <Legend 
              verticalAlign="bottom" 
              height={50}
              iconType="line"
              wrapperStyle={{ paddingTop: '20px', fontSize: '11px' }}
              formatter={(value) => (
                <span style={{ color: '#6b7280', fontSize: '11px' }}>
                  {value.length > 18 ? value.substring(0, 18) + '...' : value}
                </span>
              )}
            />
            {roleNames.map((role, index) => (
              <Line
                key={role}
                type="monotone"
                dataKey={role}
                stroke={roleColors[index % roleColors.length]}
                strokeWidth={3}
                dot={{ r: 5, strokeWidth: 2, fill: 'white' }}
                activeDot={{ r: 7, strokeWidth: 2 }}
                connectNulls={false}
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default RoleDemandTrendChart;